// @flow

import _ from 'lodash';
import type { MergeValuesState, MergeValuesAction } from 'stores/flow-type/merge-values.type';

import {
  GET_MERGE_VALUES_TYPES_REQUEST,
  GET_MERGE_VALUES_TYPES_SUCCESS,
  GET_MERGE_VALUES_TYPES_FAILURE,
  DELETE_MERGE_VALUES_TYPE_REQUEST,
  DELETE_MERGE_VALUES_TYPE_SUCCESS,
  DELETE_MERGE_VALUES_TYPE_FAILURE,
  UPDATE_MERGE_VALUES_TYPE_STATUS_REQUEST,
  UPDATE_MERGE_VALUES_TYPE_STATUS_SUCCESS,
  UPDATE_MERGE_VALUES_TYPE_STATUS_FAILURE,
  GET_MERGE_VALUES_BY_TYPE_REQUEST,
  GET_MERGE_VALUES_BY_TYPE_SUCCESS,
  GET_MERGE_VALUES_BY_TYPE_FAILURE,
  SAVE_MERGE_VALUES_RULE_REQUEST,
  SAVE_MERGE_VALUES_RULE_SUCCESS,
  SAVE_MERGE_VALUES_RULE_FAILURE,
  DELETE_MERGE_VALUES_RULE_REQUEST,
  DELETE_MERGE_VALUES_RULE_SUCCESS,
  DELETE_MERGE_VALUES_RULE_FAILURE,
  UPDATE_MERGE_VALUES_RULE_STATUS_REQUEST,
  UPDATE_MERGE_VALUES_RULE_STATUS_SUCCESS,
  UPDATE_MERGE_VALUES_RULE_STATUS_FAILURE,
  RESET_MERGE_VALUE_ITEM
} from 'stores/flow-type/merge-values.type';

const initialState: MergeValuesState = {
  getTypesLoading: false,
  getTypesSuccess: false,
  getLoading: false,
  getSuccess: false,
  updating: false,
  deleting: false,
  success: false,
  error: undefined,
  types: [],
  rules: [],
  scheduleSyncIn: 0,
  scheduleSyncAfter: 0,
  waitSyncAt: 0
};

const mergeValuesReducers = (state: MergeValuesState = initialState, action: MergeValuesAction) => {
  switch (action.type) {
    case GET_MERGE_VALUES_TYPES_REQUEST:
      return {
        ...state,
        getTypesLoading: true,
        getTypesSuccess: false,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case GET_MERGE_VALUES_TYPES_SUCCESS: {
      const { data = {} } = action.payload;
      const { data: types = [], waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;

      return {
        ...state,
        getTypesLoading: false,
        getTypesSuccess: true,
        error: undefined,
        types,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case GET_MERGE_VALUES_TYPES_FAILURE:
      return {
        ...state,
        getTypesLoading: false,
        getTypesSuccess: false,
        error: action.payload
      };

    case DELETE_MERGE_VALUES_TYPE_REQUEST:
      return {
        ...state,
        deleting: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };

    case DELETE_MERGE_VALUES_TYPE_SUCCESS: {
      const { data = {}, type = '' } = action.payload;
      const { waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;
      const { types } = state;
      _.remove(types, { type });
      return {
        ...state,
        deleting: false,
        success: true,
        error: undefined,
        types: [...types],
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case DELETE_MERGE_VALUES_TYPE_FAILURE:
      return {
        ...state,
        deleting: false,
        success: false,
        error: action.payload
      };

    case UPDATE_MERGE_VALUES_TYPE_STATUS_REQUEST: {
      const { type, status } = action.payload;
      const { types } = state;
      const index = !_.isEmpty(type) ? _.findIndex(types, { type }) : -1;
      if (index !== -1) {
        types[index].status = status;
      }

      return {
        ...state,
        types: [...types],
        updating: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    }
    case UPDATE_MERGE_VALUES_TYPE_STATUS_SUCCESS: {
      const { data = {} } = action.payload;
      const { waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;
      return {
        ...state,
        updating: false,
        success: true,
        error: undefined,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case UPDATE_MERGE_VALUES_TYPE_STATUS_FAILURE: {
      const { type, status } = action.payload;
      const { types } = state;
      const index = !_.isEmpty(type) ? _.findIndex(types, { type }) : -1;
      if (index !== -1) {
        types[index].status = status === 'active' ? 'disabled' : 'active';
      }

      return {
        ...state,
        types: [...types],
        updating: false,
        success: false,
        error: action.payload
      };
    }
    case GET_MERGE_VALUES_BY_TYPE_REQUEST:
      return {
        ...state,
        getLoading: true,
        getSuccess: false,
        rules: []
      };
    case GET_MERGE_VALUES_BY_TYPE_SUCCESS: {
      const { data: rules = [] } = action.payload.data;
      return {
        ...state,
        getLoading: false,
        getSuccess: true,
        error: undefined,
        rules
      };
    }
    case GET_MERGE_VALUES_BY_TYPE_FAILURE:
      return {
        ...state,
        getLoading: false,
        getSuccess: false,
        error: action.payload
      };

    case SAVE_MERGE_VALUES_RULE_REQUEST:
      return {
        ...state,
        updating: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };

    case SAVE_MERGE_VALUES_RULE_SUCCESS: {
      const { data = {}, id } = action.payload;
      const { data: rule, waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;
      const { rules } = state;
      if (_.isEmpty(id)) {
        rules.unshift(rule);
      } else {
        const index = _.findIndex(rules, rule => rule.id === id);
        rules[index] = rule;
      }
      return {
        ...state,
        updating: false,
        success: true,
        error: undefined,
        rules,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case SAVE_MERGE_VALUES_RULE_FAILURE:
      return {
        ...state,
        updating: false,
        success: false,
        error: action.payload
      };

    case DELETE_MERGE_VALUES_RULE_REQUEST:
      return {
        ...state,
        deleting: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };

    case DELETE_MERGE_VALUES_RULE_SUCCESS: {
      const { data = {}, id = '' } = action.payload;
      const { waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;
      const { rules } = state;
      _.remove(rules, { id });
      return {
        ...state,
        deleting: false,
        success: true,
        error: undefined,
        rules,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case DELETE_MERGE_VALUES_RULE_FAILURE:
      return {
        ...state,
        deleting: false,
        success: false,
        error: action.payload
      };

    case UPDATE_MERGE_VALUES_RULE_STATUS_REQUEST: {
      const { id, status } = action.payload;
      const { rules } = state;
      const index = !_.isEmpty(id) ? _.findIndex(rules, { id }) : -1;
      if (index !== -1) {
        rules[index].status = status;
      }

      return {
        ...state,
        rules: [...rules],
        updating: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    }
    case UPDATE_MERGE_VALUES_RULE_STATUS_SUCCESS: {
      const { data = {} } = action.payload;
      const { waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;
      return {
        ...state,
        updating: false,
        success: true,
        error: undefined,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case UPDATE_MERGE_VALUES_RULE_STATUS_FAILURE: {
      const { id, status } = action.payload;
      const { rules } = state;
      const index = !_.isEmpty(id) ? _.findIndex(rules, { id }) : -1;
      if (index !== -1) {
        rules[index].status = status === 'active' ? 'disabled' : 'active';
      }

      return {
        ...state,
        rules: [...rules],
        updating: false,
        success: false,
        error: action.payload
      };
    }
    case RESET_MERGE_VALUE_ITEM:
      return {
        ...state,
        rules: []
      };
    default:
      return state;
  }
};
export default mergeValuesReducers;
